import Popup from '@/components/Common/DesignSystem/Popup/Popup';
import DotLoader from '@/components/Common/DotLoader/DotLoader';
import Share, { ShareChannel } from '@/components/Common/Share';
import { SunnyFC } from '@/components/Common/SunnyNext';
import { withSunnyPage } from '@/components/Common/SunnyPage';
import { T_RUN_MODE } from '@/configs/getConfig';
import { IMemberLiveCommerceTokenPayload } from '@/models/Member/interfaces/payloads/IMemberLiveCommerceTokenPayload';
import { EN_PAGE_NAME } from '@/services/SEOService/metaData';
import { generatePageMeta } from '@/services/SEOService/metaGenerator';
import { Kinesis } from '@/services/TrackerService';
import { GlobalStoreCtx } from '@/stores/GlobalStore';
import { forceBackFromExternal, getPageUrl } from '@/utils';
import {
  EN_SAUCEFLEX_PLAYER_MESSAGE,
  getBroadcastInfo,
  getLinkFrom,
  goTo,
  IBroadcastInfo,
} from '@/utils/domain/livecommerce';
import { EN_APP_DEVICE } from '@/utils/functions/device/EN_APP_DEVICE';
import { useYaNative } from '@/utils/hooks/useYaNative';
import { nothing } from '@/utils/yambda';
import { identity } from 'lodash';
import qs from 'qs';
import { prop } from 'ramda';
import React, { useContext, useEffect, useRef, useState } from 'react';
import styles from './[broadcastId].module.scss';
import debounce from 'lodash/debounce';
import { isFeatureEnabled } from '@/utils/feature-flag';
import FeJourneyActions from '@/models/FeJourney/FeJourneyAction';
import FeJourneyRequestBuilder from '@/models/FeJourney/FeJourneyRequestBuilder';
import clsx from 'clsx';

export interface IIframeMessageLegacyPayload {
  key: string;
  params: {
    linkUrl: string | null;
    broadcastIdx?: string;
    bannerId?: string;
    productId?: string;
  } | null;
}

/**
 * iframe 메시지 페이로드 구조
 */
export interface IIframeMessagePayload {
  key: string;
  params: IIframeMessageParams | null;
}

/**
 * iframe 메시지 페이로드 구조
 */
export interface IIframeMessagePayload {
  key: string;
  params: IIframeMessageParams | null;
}

/**
 * 가능한 모든 스킴 타입의 union 정의
 */
export type IIframeMessageParams =
  | BannerSchemeParams
  | ProductSchemeParams
  | BannerSchemeAppParams
  | ProductSchemeAppParams
  | CouponSchemeParams;

/**
 * 배너 클릭 앱스킴 구조 (Web)
 */
export interface BannerSchemeParams {
  linkUrl: string; // 상품 링크
  broadcastIdx: string; // 방송 ID
  bannerId: string; // 배너 ID
}

/* 상품 클릭 앱스킴 구조 (Web) */
export interface ProductSchemeParams {
  linkUrl: string; // 상품 링크
  broadcastIdx: string; // 방송 ID
  price: string | number; // 상품 가격
  externalProductId: string | null; // 상품 코드
  isSoldout: boolean; // 품절 상태
}

/**
 * 배너 클릭 앱스킴 공통 구조 (Android / iOS 동일)
 */
export interface BannerSchemeAppParams {
  url: string; // 앱 전용 링크 (Android/iOS)
  broadcastIdx: string; // 방송 ID
  bannerId: string; // 배너 ID
  webUrl: string | null; // Fallback용 Web URL 링크
}

/**
 * 상품 클릭 앱스킴 공통 구조 (Android / iOS 동일)
 */
export interface ProductSchemeAppParams {
  url: string; // 앱 전용 링크 (Android/iOS)
  broadcastIdx: string; // 방송 ID
  price: string | number; // 상품 가격
  externalProductId: string | null; // 상품 코드
  isSoldout: boolean; // 품절 상태
  webUrl: string | null; // Fallback용 Web URL 링크
}

/**
 * 쿠폰 클릭 앱스킴 구조
 */
export interface CouponSchemeParams {
  broadcastIdx: string; // 방송 ID
  couponCode: string | null; // 쿠폰 코드
  couponId: string | null; // 등록한 쿠폰 ID
  couponName: string | null; // 등록한 쿠폰명
  couponType: 'link' | 'newWindow' | 'download' | 'custom' | 'apiDownload'; // 쿠폰 타입
  linkUrl: string | null; // 링크 이동 URL
  metadata: string | null; // 클릭 이벤트 사용자 설정시의 전달 데이터 값
  schemeUrl?: string | null; // scheme URL
}

interface ILiveCommerceDetailProps {
  isNotSupportedOS: boolean;
  envMode: T_RUN_MODE;
  appVersion: string;
  broadcastId: string;
  broadcastStateCode: string | undefined;
  startWithPIP: boolean;
  accessToken?: IMemberLiveCommerceTokenPayload;
  accountSiteUrl: string;
  yanoljaSiteUrl: string;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const createClickLogData = (type: EN_SAUCEFLEX_PLAYER_MESSAGE, item: IIframeMessagePayload, isApp: boolean) => {
  const params = item.params!;

  if (
    type === EN_SAUCEFLEX_PLAYER_MESSAGE.BANNER ||
    type === EN_SAUCEFLEX_PLAYER_MESSAGE.BANNER_ANDROID ||
    type === EN_SAUCEFLEX_PLAYER_MESSAGE.BANNER_IOS
  ) {
    const bannerParams = params as BannerSchemeParams | BannerSchemeAppParams;
    return {
      object: bannerParams.bannerId,
      objectType: 'bannerId',
      objectContainer: 'promoBanner',
      bannerUrl: isApp ? (bannerParams as BannerSchemeAppParams).url : (bannerParams as BannerSchemeParams).linkUrl,
    };
  }

  if (
    type === EN_SAUCEFLEX_PLAYER_MESSAGE.PRODUCT ||
    type === EN_SAUCEFLEX_PLAYER_MESSAGE.PRODUCT_ANDROID ||
    type === EN_SAUCEFLEX_PLAYER_MESSAGE.PRODUCT_IOS
  ) {
    const productParams = params as ProductSchemeParams | ProductSchemeAppParams;
    return {
      object: productParams.externalProductId || '',
      objectType: 'productIdSauceFlex',
      objectContainer: 'productList',
      productUrl: isApp
        ? (productParams as ProductSchemeAppParams).url
        : (productParams as ProductSchemeParams).linkUrl,
    };
  }

  if (
    type === EN_SAUCEFLEX_PLAYER_MESSAGE.COUPON ||
    type === EN_SAUCEFLEX_PLAYER_MESSAGE.COUPON_ANDROID ||
    type === EN_SAUCEFLEX_PLAYER_MESSAGE.COUPON_IOS
  ) {
    const couponParams = params as CouponSchemeParams;
    return {
      object: couponParams.couponId || '',
      objectType: 'couponBannerId',
      objectContainer: 'promoBanner',
      bannerUrl: isApp ? couponParams.schemeUrl : couponParams.linkUrl,
    };
  }
};

/** PIP 전용 usepip 쿼리를 제거한 URL을 만든다. 로그인 복귀 URL처럼 최초 진입 이후에 재사용되는 링크에 쓴다. */
const withoutUsePip = (href: string): string => {
  try {
    const url = new URL(href);
    url.searchParams.delete('usepip');
    return url.toString();
  } catch {
    return href; // 절대 URL 파싱 실패 시 원본 그대로
  }
};

/**
 * 닫기/뒤로가기 시 accounts(로그인 퍼널)에서 복귀한 경우를 back 대상에서 제외하기 위한 패턴.
 * stage-accounts·dev-accounts 모두 이 부분 문자열을 포함한다.
 */
const ACCOUNTS_REFERRER = /accounts\.yanolja\.com/;

const LiveCommerceDetail: SunnyFC<ILiveCommerceDetailProps> = ({
  isNotSupportedOS,
  envMode,
  appVersion,
  broadcastId,
  broadcastStateCode,
  accessToken,
  startWithPIP,
  accountSiteUrl,
  yanoljaSiteUrl,
}) => {
  const globalStore = useContext(GlobalStoreCtx);
  const yaNative = useYaNative();
  const kinesisPageName = 'Broadcast';
  // Android System WebView 144+ env(safe-area-inset-*) 회피용 modifier
  const isAndroidWebView = globalStore.AppDevice === EN_APP_DEVICE.ANDROID;
  // 공유 URL에 usepip가 남으면 공유받은 사람도 PIP로 진입해버린다.
  const shareLink = process.browser ? withoutUsePip(window.location.href) : '';
  const baseLogData = {
    broadcastId,
    screenMode: 'full',
    broadcastStateCode,
  };

  const [isLoading, setIsLoading] = useState(true);
  const [isOpenShare, setIsOpenShare] = useState(false);
  const [isOpenPopup, setIsOpenPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const [playerLink, setPlayerLink] = useState('');
  const loadingRef = useRef<boolean>(isLoading);

  useEffect(() => {
    /**
     * Safari 에서 뒤로가기시 BFCache(Back Forward Cache) 때문에 스크립트 로드가 되지 않는다.
     * BFCache를 관찰하는 이벤트인 'pageshow', 'pagehide' 를 이용해 페이지가 복원된 경우 리로드해준다.
     */
    const reload = (e): void => {
      if (e.persisted || (window.performance && window.performance.navigation.type === 2)) {
        window.location.reload();
      }
    };
    window.addEventListener('pageshow', reload);

    if (isNotSupportedOS) {
      setPopupMessage('해당 OS 버전에서는 라이브 방송이 지원되지 않습니다');
      setIsOpenPopup(true);
      return;
    }

    Kinesis.view(kinesisPageName, {
      ...baseLogData,
    });

    const env = envMode === 'production' ? '' : 'stage.';
    setPlayerLink(
      `https://${env}player.sauceflex.com/broadcast/${broadcastId}${qs.stringify(
        { accessToken: accessToken?.accessToken || 'guest', fc: 'parent_fc_value' },
        { addQueryPrefix: true },
      )}`,
    );

    // 10초 이상 로딩되는 경우 팝업창을 노출한다.
    // setTimeout 세팅 시점의 state 를 가져오기 때문에 state 값을 ref로 지정해 접근한다.
    setTimeout(() => {
      if (loadingRef.current) {
        setPopupMessage('다시 시도해주세요');
        setIsOpenPopup(true);
      }
    }, 10000);

    return () => window.removeEventListener('pageshow', reload);
  }, []);

  useEffect(() => {
    if (broadcastStateCode === undefined) {
      setPopupMessage('방송 조회를 실패하였습니다.\n다시 시도해주세요.');
      setIsOpenPopup(true);
      return;
    }
    const isPreview = ['accepted', 'ready', 'standby', 'rehearsal', 'rehearsalonair'].includes(broadcastStateCode);
    yaNative?.isEnabled && yaNative.broadcastState({ isPlaying: !isPreview });

    // PIP ON / OFF — 앱(webview)뿐 아니라 웹(usepip=true)에서도 동작하도록 정의한다.
    // iframe은 호출 시점에 조회한다(effect 실행 시점엔 아직 playerLink 렌더 전일 수 있음).
    (window as any).sauceflexPictureInPictureOn = () => {
      baseLogData.screenMode = 'pip';
      const iframe = document.getElementById('live-commerce-broadcast') as HTMLIFrameElement | null;
      iframe?.contentWindow?.postMessage('sauceflexPictureInPictureOn', '*');
    };

    (window as any).sauceflexPictureInPictureOff = () => {
      baseLogData.screenMode = 'full';
      const iframe = document.getElementById('live-commerce-broadcast') as HTMLIFrameElement | null;
      iframe?.contentWindow?.postMessage('sauceflexPictureInPictureOff', '*');
    };

    // webview 리로딩은 앱(yaNative) 전용
    if (yaNative?.isEnabled) {
      (window as any).sauceflexWebviewReloading = () => {
        const iframe = document.getElementById('live-commerce-broadcast') as HTMLIFrameElement | null;
        iframe?.contentWindow?.postMessage('sauceflexWebviewReloading', '*');
      };
    }

    const handleMessage = (e: MessageEvent): void => {
      if (isFeatureEnabled('YAWEB-5731-nol-live-bridge')) {
        const parsedData: IIframeMessagePayload =
          typeof e.data === 'string' && e.data !== 'undefined' ? JSON.parse(e.data || '{}') : e.data;

        switch (parsedData.key) {
          case EN_SAUCEFLEX_PLAYER_MESSAGE.ENTER:
            return enter();
          case EN_SAUCEFLEX_PLAYER_MESSAGE.PRODUCT:
          case EN_SAUCEFLEX_PLAYER_MESSAGE.PRODUCT_ANDROID:
          case EN_SAUCEFLEX_PLAYER_MESSAGE.PRODUCT_IOS:
          case EN_SAUCEFLEX_PLAYER_MESSAGE.BANNER:
          case EN_SAUCEFLEX_PLAYER_MESSAGE.BANNER_ANDROID:
          case EN_SAUCEFLEX_PLAYER_MESSAGE.BANNER_IOS:
            return handeClickItem(parsedData.key, parsedData);
          case EN_SAUCEFLEX_PLAYER_MESSAGE.EXIT:
            return exit();
          case EN_SAUCEFLEX_PLAYER_MESSAGE.SHARE:
            return share();
          case EN_SAUCEFLEX_PLAYER_MESSAGE.PIP:
            return changeToPip();
          case EN_SAUCEFLEX_PLAYER_MESSAGE.LOGIN:
            return login();
          case EN_SAUCEFLEX_PLAYER_MESSAGE.INPUTBLUR:
            return scrollTop();
          case EN_SAUCEFLEX_PLAYER_MESSAGE.COUPON:
          case EN_SAUCEFLEX_PLAYER_MESSAGE.COUPON_ANDROID:
          case EN_SAUCEFLEX_PLAYER_MESSAGE.COUPON_IOS:
            return clickCoupon(parsedData.key, parsedData);
          default:
            return;
        }
      } else {
        const parsedData: IIframeMessagePayload = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;

        switch (parsedData.key) {
          case EN_SAUCEFLEX_PLAYER_MESSAGE.ENTER:
            return enter();
          case EN_SAUCEFLEX_PLAYER_MESSAGE.PRODUCT:
          case EN_SAUCEFLEX_PLAYER_MESSAGE.BANNER:
            return clickItem(parsedData as IIframeMessageLegacyPayload);
          case EN_SAUCEFLEX_PLAYER_MESSAGE.EXIT:
            return exit();
          case EN_SAUCEFLEX_PLAYER_MESSAGE.SHARE:
            return share();
          case EN_SAUCEFLEX_PLAYER_MESSAGE.PIP:
            return changeToPip();
          case EN_SAUCEFLEX_PLAYER_MESSAGE.LOGIN:
            return login();
          case EN_SAUCEFLEX_PLAYER_MESSAGE.INPUTBLUR:
            return scrollTop();
          default:
            return;
        }
      }
    };

    window.addEventListener('message', handleMessage, false);
    return () => window.removeEventListener('message', handleMessage);
  }, [yaNative]);

  // 방송 입장 시
  const enter = (): void => {
    setIsLoading(false);
    loadingRef.current = false;

    // PIP 파라미터가 전달되면 PIP 모드로 시작한다. (앱/웹 공통)
    if (startWithPIP) {
      (window as any).sauceflexPictureInPictureOn?.();
    }
  };

  const handeClickItem = (type: EN_SAUCEFLEX_PLAYER_MESSAGE, item: IIframeMessagePayload): void => {
    if (!item.params) return;

    const isApp = !!yaNative?.isEnabled;
    const logData = createClickLogData(type, item, isApp);

    Kinesis.click(kinesisPageName, { ...logData, platform: isApp ? 'app' : 'web', ...baseLogData });

    if (yaNative?.isEnabled && globalStore.AppDevice === EN_APP_DEVICE.IOS) {
      yaNative?.requestPipMode();
    }

    let link = '';
    if (isApp) {
      link =
        (item.params as BannerSchemeAppParams | ProductSchemeAppParams).url ||
        (item.params as BannerSchemeAppParams | ProductSchemeAppParams).webUrl ||
        '';
    } else {
      const linkUrl = (item.params as BannerSchemeParams | ProductSchemeParams).linkUrl;
      // MW 의 경우 sauceflexProductAndroidScheme 또는 sauceflexProductIosScheme 를 따르게 되므로 webUrl을 비교해야 합니다.
      const webUrl = (item.params as ProductSchemeAppParams).webUrl;
      link = getLinkFrom(linkUrl || webUrl || '').weblink;
    }

    location.href = link;
  };

  // 방송 배너 클릭 or 상품 클릭
  const clickItem = (item: IIframeMessageLegacyPayload): void => {
    const link = getLinkFrom(item.params?.linkUrl || '');

    const logData =
      item.key === EN_SAUCEFLEX_PLAYER_MESSAGE.PRODUCT
        ? {
            object: item.params?.productId,
            objectType: 'productIdSauceFlex',
            objectContainer: 'productList',
            productUrl: yaNative?.isEnabled ? link.deeplink : link.weblink,
          }
        : {
            object: item.params?.bannerId,
            objectType: 'bannerId',
            objectContainer: 'promoBanner',
          };

    Kinesis.click(kinesisPageName, {
      ...logData,
      ...baseLogData,
      bannerUrl: yaNative?.isEnabled ? link.deeplink : link.weblink,
      platform: yaNative?.isEnabled ? 'app' : 'web',
    });

    goTo(link, globalStore.AppDevice);
  };

  const clickCoupon = (type: EN_SAUCEFLEX_PLAYER_MESSAGE, item: IIframeMessagePayload): void => {
    if (!item.params) return;

    const params = item.params as CouponSchemeParams;
    const isApp = !!yaNative?.isEnabled;
    const logData = createClickLogData(type, item, isApp);

    Kinesis.click(kinesisPageName, { ...logData, platform: isApp ? 'app' : 'web', ...baseLogData });

    if (yaNative?.isEnabled && globalStore.AppDevice === EN_APP_DEVICE.IOS) {
      yaNative?.requestPipMode();
    }

    let link = '';
    if (isApp) {
      // app
      link = params.schemeUrl || params.linkUrl || '';
    } else {
      // pc web, mweb(type이 sauceflexCouponAndroidScheme, sauceflexCouponIosScheme로 들어오기때문에 linkUrl을 사용해야함)
      link = params.linkUrl || '';
    }

    location.href = link;
  };

  // PIP 모드 전환
  const changeToPip = (): void => {
    const [major, minor] = (appVersion || '').split('.').map((v) => +v);

    // 앱 8.4.0 버전 이하에서는 PIP 버튼 클릭 시 팝업창 노출
    if (major <= 8 && minor < 4) {
      const iframe = document.getElementById('live-commerce-broadcast') as HTMLIFrameElement;
      iframe.contentWindow?.postMessage('sauceflexPictureInPictureOff', '*');
      setPopupMessage('최신 버전에서만 사용 가능합니다.');
      setIsOpenPopup(true);
      return;
    }

    yaNative?.isEnabled && yaNative.requestPipMode();
  };

  // 방송 나가기
  const exit = debounce((): void => {
    Kinesis.click(kinesisPageName, {
      ...baseLogData,
      object: 'closeBtn',
      objectContainer: 'naviBar',
    });

    if (yaNative?.isEnabled) {
      yaNative.dismiss();
    } else {
      leaveWeb();
    }
  }, 500);

  /**
   * 웹에서 페이지를 떠날 때의 목적지.
   * referrer가 야놀자 웹(accounts 로그인 퍼널 제외)이면 history.back(), 아니면 NOL 홈으로 보낸다.
   * 로그인 복귀·외부 유입·직접 진입 시 뒤로가기가 로그인 페이지나 엉뚱한 곳으로 가는 것을 막는다.
   */
  const leaveWeb = (): void => forceBackFromExternal(yanoljaSiteUrl, ACCOUNTS_REFERRER);

  // 공유하기
  const share = (): void => {
    setIsOpenShare(true);
  };
  const onShare = (shareChannel: ShareChannel): void => {
    Kinesis.click(kinesisPageName, {
      ...baseLogData,
      object: 'shareBtn',
      objectContainer: 'bottomSheet',
      shareChannel,
    });
  };

  // 로그인
  const login = (): void => {
    Kinesis.click(kinesisPageName, {
      ...baseLogData,
      object: 'loginBtn',
      objectContainer: 'loginPopup',
    });

    // usepip는 최초 진입 시에만 유효한 쿼리다. 그대로 두면 로그인 복귀 후 다시 PIP로 시작해버린다.
    const redirectUrl = withoutUsePip(location.href);

    if (yaNative?.isEnabled) {
      globalStore.AppDevice === EN_APP_DEVICE.IOS && yaNative.requestPipMode();
      yaNative.login({ method: 'GET', url: redirectUrl });
    } else {
      window.location.replace(`${accountSiteUrl}?service=yanolja&redirectUrl=${encodeURIComponent(redirectUrl)}`);
    }
  };

  const scrollTop = (): void => {
    // iOS 특정 기기에서 키패드를 내렸을 경우 화면이 내려오지 않는 이슈가 있어 스크롤을 이동한다
    scrollTo(0, 0);
  };

  const closePopup = (): void => {
    setIsOpenPopup(false);

    // 재시도 안내 팝업인 경우 처리
    if (isLoading) {
      yaNative?.isEnabled ? yaNative.dismiss() : leaveWeb();
    }
  };

  return (
    <main className={clsx(styles.container, isAndroidWebView && styles.isAndroidWebView)}>
      {isLoading && <DotLoader className={styles.loading} />}
      {playerLink && (
        <iframe
          id="live-commerce-broadcast"
          src={playerLink}
          className={styles.player}
          frameBorder="0"
          allow="autoplay"
        />
      )}
      <Share
        isOpen={isOpenShare}
        url={shareLink}
        hideFacebook={yaNative?.isEnabled}
        onRequestClose={() => setIsOpenShare(false)}
        onShare={onShare}
      />
      <Popup isOpen={isOpenPopup} mainBtn={{ label: '확인', onClick: closePopup }}>
        {popupMessage}
      </Popup>
    </main>
  );
};

LiveCommerceDetail.getInitialProps = async ({ req, query }) => {
  const feJourneyActions = new FeJourneyActions(FeJourneyRequestBuilder, req);

  const startWithPIP = query.usepip === 'true';
  const os = req.ua.getOS();
  // const browser = req.ua.getBrowser();
  const [majorVersion] = (os.version || '').split('.').map((v) => +v);
  // 소스플렉스에서 지원하지 않는 디바이스 OS 버전에 대해 안내 팝업을 노출한다
  const isNotSupportedOS = (os.name === 'iOS' && majorVersion <= 11) || (os.name === 'Android' && majorVersion <= 7);
  const sauceFlexUrl = req.config.LiveCommerce.href();
  const accountSiteUrl = req.config.AccountsSite;
  const yanoljaSiteUrl = req.config.YanoljaSite;

  const {
    broadcastStateCode,
    broadcastName,
    programmingStartDt,
    thumbnailUrl,
  }: IBroadcastInfo = await getBroadcastInfo(sauceFlexUrl, query.broadcastId);
  const baseResp = {
    isNotSupportedOS,
    envMode: req.config.mode as T_RUN_MODE,
    appVersion: req.headers['version'],
    broadcastId: query.broadcastId,
    broadcastStateCode,
    startWithPIP,
    accountSiteUrl,
    yanoljaSiteUrl,
    headProps: generatePageMeta({
      pageName: EN_PAGE_NAME.PAGE__LIVE_COMMERCE_DETAIL,
      pageUrl: getPageUrl(req),
      options: {
        title: `${broadcastName} ${programmingStartDt}`,
        image: thumbnailUrl,
      },
    }),
  };

  // 로그인 시 라이브 방송 토큰 생성해 연동
  if (req.session.isLoggedIn() && !isNotSupportedOS) {
    const entity = feJourneyActions.liveCommerceToken();
    const accessToken = (await entity).map(prop('data')).caseOf({
      left: nothing, // 통신 실패시 비회원으로 진입
      right: identity,
    });

    return {
      ...baseResp,
      accessToken,
    };
  }

  return {
    ...baseResp,
  };
};

export default withSunnyPage()(LiveCommerceDetail);
